import React, { useState, useEffect } from 'react';
import { adminService } from '../../services/adminService';
import { Users, BookOpen, DollarSign, BarChart3, Settings, Edit, Trash2, Eye } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import LoadingSpinner from '../shared/LoadingSpinner';
import Navbar from '../shared/Navbar';
import LevelManagement from './LevelManagement';
import TermManagement from './TermManagement';
import LessonManagement from './LessonManagement';
import ExperimentManagement from './ExperimentManagement';

const AdminDashboard = () => {
    const [activeSection, setActiveSection] = useState('dashboard');
    const [analytics, setAnalytics] = useState(null);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (activeSection === 'dashboard') {
            fetchAnalytics();
        } else if (activeSection === 'users') {
            fetchUsers();
        }
    }, [activeSection]);

    const fetchAnalytics = async () => {
        try {
            setLoading(true);
            const data = await adminService.getAnalytics();
            setAnalytics(data.analytics);
        } catch (err) {
            console.error('Failed to fetch analytics:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const data = await adminService.getUsers();
            setUsers(data.users);
        } catch (err) {
            console.error('Failed to fetch users:', err);
        } finally {
            setLoading(false);
        }
    };

    const StatCard = ({ icon: Icon, label, value, change, color }) => (
        <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
                <div className={`p-3 rounded-lg`} style={{ backgroundColor: `${color}20` }}>
                    <Icon className="w-6 h-6" style={{ color }} />
                </div>
                {change && (
                    <span className={`text-sm font-semibold ${change > 0 ? 'text-green-600' : 'text-red-600'}`}>
            {change > 0 ? '+' : ''}{change}%
          </span>
                )}
            </div>
            <p className="text-gray-500 text-sm">{label}</p>
            <p className="text-3xl font-bold mt-1">{value}</p>
        </div>
    );

    return (
        <div className="min-h-screen bg-gray-50">
            <Navbar />

            <div className="flex">
                {/* Sidebar */}
                <div className="w-64 bg-gradient-to-b from-purple-900 to-purple-700 text-white min-h-screen p-6 sticky top-0 h-screen overflow-y-auto">
                    <div className="mb-8">
                        <h1 className="text-2xl font-bold">STEM Admin</h1>
                        <p className="text-purple-200 text-sm mt-1">Platform Management</p>
                    </div>

                    <nav className="space-y-2">
                        {[
                            { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
                            { id: 'users', label: 'Users', icon: Users },
                            { id: 'levels', label: 'Levels', icon: BookOpen },
                            { id: 'terms', label: 'Terms', icon: Settings },
                            { id: 'lessons', label: 'Lessons', icon: BookOpen },
                            { id: 'experiments', label: 'Experiments', icon: Settings },
                            { id: 'revenue', label: 'Revenue', icon: DollarSign }
                        ].map(item => (
                            <button
                                key={item.id}
                                onClick={() => setActiveSection(item.id)}
                                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                                    activeSection === item.id
                                        ? 'bg-white text-purple-900 shadow-lg'
                                        : 'hover:bg-purple-800'
                                }`}
                            >
                                <item.icon className="w-5 h-5" />
                                <span className="font-medium">{item.label}</span>
                            </button>
                        ))}
                    </nav>
                </div>

                {/* Main Content */}
                <div className="flex-1 p-8 overflow-x-auto">
                    {/* Dashboard Section */}
                    {activeSection === 'dashboard' && (
                        <div className="space-y-6">
                            <div>
                                <h2 className="text-3xl font-bold text-gray-900 mb-2">Dashboard</h2>
                                <p className="text-gray-600">Platform overview and key metrics</p>
                            </div>

                            {loading ? (
                                <LoadingSpinner message="Loading analytics..." fullScreen={false} />
                            ) : analytics ? (
                                <>
                                    {/* Stats Grid */}
                                    <div className="grid md:grid-cols-3 gap-6">
                                        <StatCard
                                            icon={Users}
                                            label="Total Users"
                                            value={analytics.users?.total_users?.toLocaleString() || 0}
                                            change={12}
                                            color="#8b5cf6"
                                        />
                                        <StatCard
                                            icon={BookOpen}
                                            label="Active Students"
                                            value={analytics.lessons?.active_students?.toLocaleString() || 0}
                                            change={8}
                                            color="#10b981"
                                        />
                                        <StatCard
                                            icon={DollarSign}
                                            label="Total Revenue"
                                            value={`$${(analytics.subscriptions?.total_revenue || 0).toLocaleString()}`}
                                            change={15}
                                            color="#ec4899"
                                        />
                                    </div>

                                    {/* Charts */}
                                    <div className="bg-white rounded-xl shadow-md p-6">
                                        <h3 className="text-xl font-bold mb-4">Recent Activity (Last 30 Days)</h3>
                                        {analytics.recentActivity && analytics.recentActivity.length > 0 ? (
                                            <ResponsiveContainer width="100%" height={300}>
                                                <LineChart data={analytics.recentActivity}>
                                                    <CartesianGrid strokeDasharray="3 3" />
                                                    <XAxis dataKey="date" />
                                                    <YAxis />
                                                    <Tooltip />
                                                    <Legend />
                                                    <Line type="monotone" dataKey="activities" stroke="#8b5cf6" strokeWidth={2} />
                                                </LineChart>
                                            </ResponsiveContainer>
                                        ) : (
                                            <p className="text-center text-gray-600 py-8">No activity data available</p>
                                        )}
                                    </div>
                                </>
                            ) : (
                                <div className="card text-center py-12">
                                    <BarChart3 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                                    <p className="text-gray-600">No analytics data available</p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Users Section */}
                    {activeSection === 'users' && (
                        <div className="space-y-6">
                            <div>
                                <h2 className="text-3xl font-bold text-gray-900 mb-2">User Management</h2>
                                <p className="text-gray-600">Manage all platform users</p>
                            </div>

                            {loading ? (
                                <LoadingSpinner message="Loading users..." fullScreen={false} />
                            ) : users.length === 0 ? (
                                <div className="card text-center py-12">
                                    <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                                    <p className="text-gray-600">No users found</p>
                                </div>
                            ) : (
                                <div className="bg-white rounded-xl shadow-md overflow-hidden">
                                    <div className="overflow-x-auto">
                                        <table className="w-full">
                                            <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Joined</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                                            </tr>
                                            </thead>
                                            <tbody className="divide-y">
                                            {users.map(user => (
                                                <tr key={user.id} className="hover:bg-gray-50">
                                                    <td className="px-6 py-4">
                                                        <div>
                                                            <p className="font-semibold">{user.full_name}</p>
                                                            <p className="text-sm text-gray-500">{user.email}</p>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4">
                              <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-700">
                                {user.user_type}
                              </span>
                                                    </td>
                                                    <td className="px-6 py-4">
                              <span className={`px-2 py-1 text-xs rounded-full ${
                                  user.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                              }`}>
                                {user.is_active ? 'active' : 'inactive'}
                              </span>
                                                    </td>
                                                    <td className="px-6 py-4 text-sm text-gray-600">
                                                        {new Date(user.created_at).toLocaleDateString()}
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <div className="flex gap-2">
                                                            <button className="p-2 hover:bg-gray-100 rounded">
                                                                <Eye className="w-4 h-4 text-gray-600" />
                                                            </button>
                                                            <button className="p-2 hover:bg-gray-100 rounded">
                                                                <Edit className="w-4 h-4 text-blue-600" />
                                                            </button>
                                                            <button className="p-2 hover:bg-gray-100 rounded">
                                                                <Trash2 className="w-4 h-4 text-red-600" />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Level Management */}
                    {activeSection === 'levels' && <LevelManagement />}

                    {/* Term Management */}
                    {activeSection === 'terms' && <TermManagement />}

                    {/* Lesson Management */}
                    {activeSection === 'lessons' && <LessonManagement />}

                    {/* Experiment Management */}
                    {activeSection === 'experiments' && <ExperimentManagement />}

                    {/* Revenue Section */}
                    {activeSection === 'revenue' && (
                        <div className="space-y-6">
                            <div>
                                <h2 className="text-3xl font-bold text-gray-900 mb-2">Revenue Analytics</h2>
                                <p className="text-gray-600">Track platform revenue and subscriptions</p>
                            </div>
                            <div className="card text-center py-12">
                                <DollarSign className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                                <p className="text-gray-600">Revenue analytics coming soon</p>
                                <p className="text-sm text-gray-500 mt-2">
                                    Track subscriptions, payments, and financial metrics
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AdminDashboard;